import React from 'react';
import { tokens } from '../design';

interface LoadingStateProps {
  /**
   * Size of the loading spinner
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Text to display below the spinner
   */
  text?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Whether to show the loading state in full screen
   */
  fullScreen?: boolean;

  /**
   * Background color for the loading state
   */
  backgroundColor?: string;

  /**
   * Whether to show animated dots below the text
   */
  showDots?: boolean;
}

const LoadingState: React.FC<LoadingStateProps> = ({
  size = 'md',
  text = 'Loading...',
  className = '',
  fullScreen = false,
  backgroundColor = tokens.colors.neutral[100],
  showDots = false,
}) => {
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const containerClasses = fullScreen
    ? 'min-h-screen flex items-center justify-center'
    : 'flex items-center justify-center p-8';

  return (
    <div
      className={`${containerClasses} ${className}`}
      style={{ backgroundColor }}
      role='status'
      aria-label={text || 'Loading'}
    >
      <div className='text-center flex flex-col items-center justify-center'>
        {/* Spinner with consistent design */}
        <div className='relative mx-auto mb-4'>
          <div
            className={`${sizeClasses[size]} border-2 border-transparent border-t-[#C18E10] rounded-full animate-spin`}
            style={{
              borderTopColor: tokens.colors.primary[500],
            }}
          />
        </div>

        {text && (
          <div className='space-y-2'>
            <p
              className={`${textSizeClasses[size]} font-medium`}
              style={{
                color: tokens.colors.neutral[700],
                fontFamily: tokens.typography.fontFamily.heading,
              }}
            >
              {text}
            </p>

            {/* Animated dots */}
            {showDots && (
              <div className='flex justify-center space-x-1'>
                <div
                  className='w-2 h-2 rounded-full animate-bounce'
                  style={{
                    backgroundColor: tokens.colors.primary[500],
                    animationDelay: '0ms',
                  }}
                />
                <div
                  className='w-2 h-2 rounded-full animate-bounce'
                  style={{
                    backgroundColor: tokens.colors.primary[500],
                    animationDelay: '150ms',
                  }}
                />
                <div
                  className='w-2 h-2 rounded-full animate-bounce'
                  style={{
                    backgroundColor: tokens.colors.primary[500],
                    animationDelay: '300ms',
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingState;
